import type { SpecType } from '@local/lib';

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Copy, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import type { QualityAdjustmentTemplate } from './templateTypes';

import { ConfirmationModal, PageMeta, QualityAdjustmentsNav } from '../../components/index';
import { showErrorNotification, showSuccessNotification } from '../../utils/notifications';
import { TABLE_HEADER_STYLE } from '../quality-formulas/components/tableStyles';
import { getFormulaAdjustmentCategory, SPEC_TYPES } from '../quality-formulas/qualityFormulaMetadata';
import { usePermissions } from './hooks/usePermissions';
import { useTemplateActions, useTemplates } from './hooks/useTemplates';
import { ADJUSTMENT_CATEGORY_LABEL } from './templateTypes';

const ALL_SPECS_FILTER_VALUE = 'ALL';
const PAGE_SIZE = 20;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const getSpecLabel = (key: SpecType): string => SPEC_TYPES.find(spec => spec.key === key)?.label ?? key;

interface TemplateRowProps {
  canManage: boolean;
  onDelete: (template: QualityAdjustmentTemplate) => void;
  onDuplicate: (template: QualityAdjustmentTemplate) => void;
  template: QualityAdjustmentTemplate;
}

const TemplateRow: React.FC<TemplateRowProps> = ({ canManage, onDelete, onDuplicate, template }) => {
  const navigate = useNavigate();
  const adjustmentCategory = getFormulaAdjustmentCategory(template.config.formulaType);

  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={2}>
          <Text fw={600} size="sm">
            {template.name}
          </Text>
          {template.description ? (
            <Text c="dimmed" fz="xs" lineClamp={1}>
              {template.description}
            </Text>
          ) : null}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Badge size="sm" variant="light">
          {getSpecLabel(template.specType)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{ADJUSTMENT_CATEGORY_LABEL[adjustmentCategory]}</Text>
      </Table.Td>
      <Table.Td>
        <Badge color="gray" size="sm" variant="light">
          {template.config.formulaType}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text c="dimmed" size="sm">
          {formatDate(template.updatedAt)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <Tooltip label={canManage ? 'Edit template' : 'View template'}>
            <ActionIcon
              aria-label={canManage ? 'Edit template' : 'View template'}
              onClick={() => {
                void navigate(`/templates/${template.id}/edit`);
              }}
              variant="light"
            >
              {canManage ? <Pencil size={16} /> : <Eye size={16} />}
            </ActionIcon>
          </Tooltip>
          {canManage ? (
            <>
              <Tooltip label="Duplicate template">
                <ActionIcon
                  aria-label="Duplicate template"
                  onClick={() => {
                    onDuplicate(template);
                  }}
                  variant="light"
                >
                  <Copy size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete template">
                <ActionIcon
                  aria-label="Delete template"
                  // Literal hex (not the theme "red" token) + `filled` (solid background, not a
                  // low-alpha tint) so this renders as a solid, vivid red regardless of whether
                  // the host shell's own CSS lets Mantine's color variables resolve correctly.
                  color="#e03131"
                  onClick={() => {
                    onDelete(template);
                  }}
                  variant="filled"
                >
                  <Trash2 color="#fff" size={16} />
                </ActionIcon>
              </Tooltip>
            </>
          ) : null}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

interface TemplateListContentProps {
  canManageTemplates: boolean;
  currentPage: number;
  isLoading: boolean;
  onDelete: (template: QualityAdjustmentTemplate) => void;
  onDuplicate: (template: QualityAdjustmentTemplate) => void;
  onPageChange: (page: number) => void;
  selectedSpecType: null | SpecType;
  templates: QualityAdjustmentTemplate[] | undefined;
}

// Extracted so the loading/empty/table-of-results states are each a single, non-nested condition
// instead of a chained ternary.
const TemplateListContent: React.FC<TemplateListContentProps> = ({
  canManageTemplates,
  currentPage,
  isLoading,
  onDelete,
  onDuplicate,
  onPageChange,
  selectedSpecType,
  templates,
}) => {
  if (isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }

  const visibleTemplates = (templates ?? []).filter(
    template => selectedSpecType === null || template.specType === selectedSpecType,
  );

  if (visibleTemplates.length === 0) {
    return (
      <Paper p="lg" radius="md" style={{ borderStyle: 'dashed' }} withBorder>
        <Text c="dimmed" fz="sm" ta="center">
          {selectedSpecType === null ? 'No templates yet.' : `No ${getSpecLabel(selectedSpecType)} templates yet.`}{' '}
          {canManageTemplates ? 'Click "Create Template" to build one.' : 'Check back later.'}
        </Text>
      </Paper>
    );
  }

  const totalPages = Math.ceil(visibleTemplates.length / PAGE_SIZE);
  const paginatedTemplates = visibleTemplates.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <Stack gap="sm">
      <Table highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={TABLE_HEADER_STYLE}>Template Name</Table.Th>
            <Table.Th style={TABLE_HEADER_STYLE}>Quality Specification</Table.Th>
            <Table.Th style={TABLE_HEADER_STYLE}>Adjustment Type</Table.Th>
            <Table.Th style={TABLE_HEADER_STYLE}>Formula Type</Table.Th>
            <Table.Th style={TABLE_HEADER_STYLE}>Last Modified</Table.Th>
            <Table.Th style={{ ...TABLE_HEADER_STYLE, textAlign: 'center' }}>Action</Table.Th>
            <Table.Th style={TABLE_HEADER_STYLE} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginatedTemplates.map(template => (
            <TemplateRow
              canManage={canManageTemplates}
              key={template.id}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              template={template}
            />
          ))}
        </Table.Tbody>
      </Table>

      {totalPages > 1 ? (
        <Group justify="center">
          <Pagination onChange={onPageChange} size="sm" total={totalPages} value={currentPage} />
        </Group>
      ) : null}
    </Stack>
  );
};

/**
 * The Template Manager screen — a dedicated home for managing reusable Quality Adjustment
 * Templates, kept intentionally separate from Contract Entry (`QualityFormulasPage.tsx`) so
 * editing a contract can never accidentally change a shared template (see `QualityAdjustmentsNav`
 * for how users switch between the two).
 *
 * Every template belongs to exactly one Quality Specification (see `templateTypes.ts`), so the
 * spec filter below is generated straight from `SPEC_TYPES` — adding a new spec there is all a new
 * Quality Specification needs to show up here too.
 */
export const TemplateManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: templates, isLoading } = useTemplates();
  const { duplicateTemplate, removeTemplate } = useTemplateActions();
  const { canManageTemplates } = usePermissions();
  const [pendingDelete, setPendingDelete] = useState<null | QualityAdjustmentTemplate>(null);
  const [isDeleting, { close: stopDeleting, open: startDeleting }] = useDisclosure(false);
  const [specFilter, setSpecFilter] = useState<string>(ALL_SPECS_FILTER_VALUE);
  const [currentPage, setCurrentPage] = useState(1);

  const selectedSpecType =
    specFilter === ALL_SPECS_FILTER_VALUE
      ? null
      : // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
        (specFilter as SpecType);
  const specFilterOptions = [
    { label: 'All Quality Specifications', value: ALL_SPECS_FILTER_VALUE },
    ...SPEC_TYPES.map(spec => ({ label: spec.label, value: spec.key })),
  ];

  const handleDuplicate = async (template: QualityAdjustmentTemplate) => {
    try {
      const duplicate = await duplicateTemplate(template.id);
      showSuccessNotification(`Duplicated "${template.name}".`);
      void navigate(`/templates/${duplicate.id}/edit`);
    } catch (error) {
      showErrorNotification(error, 'Unable to duplicate template');
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    startDeleting();
    try {
      await removeTemplate(pendingDelete.id);
      showSuccessNotification(`Deleted "${pendingDelete.name}".`);
    } catch (error) {
      showErrorNotification(error, 'Unable to delete template');
    } finally {
      stopDeleting();
      setPendingDelete(null);
    }
  };

  return (
    <Box maw={1180} mx="auto" pb="xl" pt="sm" px="md" w="100%">
      <PageMeta
        description="Manage reusable, spec-scoped Quality Adjustment templates contracts can load as a starting point."
        title="Template Manager"
      />
      <Stack gap="lg">
        <QualityAdjustmentsNav />
        <Group align="flex-start" justify="space-between" wrap="wrap">
          <Stack gap={4} maw={640}>
            <Title fz={{ base: 22, sm: 28 }} order={1}>
              Template Manager
            </Title>
            <Text c="dimmed" size="sm">
              Manage reusable, spec-scoped Quality Adjustment templates. Contract Entry can load a template's values as
              a starting point — editing a contract never changes the template it came from.
            </Text>
          </Stack>
          {canManageTemplates ? (
            <Button
              leftSection={<Plus size={16} />}
              onClick={() => {
                void navigate('/templates/new');
              }}
            >
              Create Template
            </Button>
          ) : null}
        </Group>

        {canManageTemplates ? null : (
          <Paper bg="var(--mantine-color-default-hover)" p="sm" radius="md" withBorder>
            <Text c="dimmed" fz="sm">
              You have read-only access to templates. Only Admin users can create, edit, delete, or duplicate them — you
              can still load any template onto a contract from Contract Entry.
            </Text>
          </Paper>
        )}

        <Select
          data={specFilterOptions}
          label="Filter by Quality Specification"
          maw={320}
          onChange={value => {
            setSpecFilter(value ?? ALL_SPECS_FILTER_VALUE);
            setCurrentPage(1);
          }}
          value={specFilter}
        />

        <TemplateListContent
          canManageTemplates={canManageTemplates}
          currentPage={currentPage}
          isLoading={isLoading}
          onDelete={setPendingDelete}
          onDuplicate={next => {
            void handleDuplicate(next);
          }}
          onPageChange={setCurrentPage}
          selectedSpecType={selectedSpecType}
          templates={templates}
        />
      </Stack>

      <ConfirmationModal
        confirmLoading={isDeleting}
        confirmText="Delete"
        onCancel={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        opened={pendingDelete !== null}
        title="Delete template?"
      >
        {pendingDelete
          ? `Delete "${pendingDelete.name}"? Contracts that already loaded it keep their values — this can't be undone.`
          : ''}
      </ConfirmationModal>
    </Box>
  );
};
